/**
 * @module lib/commercial/keytar/Hermes
 */
import logger from "utils/logger";
import { API_DS_DYNAMIC_PAYWALL, API_DS_SMARTWALL } from "lib/gamservices";
import { getUserSophiId, getSophi3Id, getUserHashId, isRegistered, isAnonymous } from "lib/identity/api";
import { mark, measure, getEntriesByName } from "lib/performance/user-timing";
import tgamConfig from "config/tgam";
import { getProp, isTimedDisabled } from "utils/utils";
import { isHousePriceDataCentre, isFreeToViewReferrer } from "lib/commercial/keytar/helpers";
import values from "lodash/values";
import { local, isLocalStorageTypeSupported } from "lib/store";
import isNil from "lodash/isNil";
import { format } from "date-fns";

const debugTag = "[HERMES]";
let didTimeOut = false;

/**
 * Returns a resolved or rejected Promise depending on the Response received
 * @param {Response} response - a fetch response
 * @returns {Promise}
 */
function responseHandler(response) {
  if (response.ok) {
    return Promise.resolve(response.json());
  }
  return Promise.reject(new Error(`Hermes responded with a ${response.status} response code.`));
}

/**
 * Returns a rejected Promise when a timeout is reached
 * @param {Number} limit  - the timeout limit
 * @returns {Promise}
 */
function fetchTimeout(limit) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      didTimeOut = true;
      reject(new Error(`Hermes failed to respond within ${limit}ms.`));
    }, limit);
  });
}

/**
 * Hermes - The emissary and messenger of the gods. The divine trickster.
 * The god of boundaries and the transgression of boundaries.
 * @class
 * @constructor
 * @public
 * @see https://confluence.theglobeandmail.com/display/DataScience/Dynamic+PayWall+API
 */
export default class Hermes {
  constructor() {
    this.props = {
      forceRun: Hermes.forceRun,
      forceMock: Hermes.forceMock,
      colour: getProp(tgamConfig, "meta.paywallStatus", null),
      contentId: getProp(tgamConfig, "meta._id", null),
      advContent: getProp(tgamConfig, "meta.advContent", false),
      keywords: getProp(tgamConfig, "meta.keywords", ""),
      requestTimeout: getProp(tgamConfig, "hermes.apiTimeout", 0),
      sampleRate: {
        ANON: getProp(tgamConfig, "hermes.anonTrafficApi2", 0),
        ANON_V3: getProp(tgamConfig, "hermes.anonTrafficApi3", 0),
        REG: getProp(tgamConfig, "hermes.regTrafficApi2", 0),
        REG_V3: getProp(tgamConfig, "hermes.regTrafficApi3", 0)
      },
      sophiId: getUserSophiId(),
      sophi3Id: getSophi3Id(),
      hashId: getUserHashId(),
      isTurnedOn: getProp(tgamConfig, "hermes.enabled", "false") === "true",
      section: this.getSection(getProp(tgamConfig, "meta.mainSection", "")),
      localTime: this.getLocalTime(new Date()),
      referrer: this.getReferrer(getProp(tgamConfig, "datalayer.page.referrer.host", "")),
      pageType: getProp(tgamConfig, "meta.pagetype", null),
      // The "hermesHideSubscriberKeys" runtime property should only be set to
      // "true" when the sample rate has been increased to 100%, otherwise the
      // UX will be inconsistent across pages because the sample number is
      // randomly generated
      hideSubscriberKeys: getProp(tgamConfig, "hermes.hideSubscriberKeys", "false") === "true",
      passDataToKeytar: getProp(tgamConfig, "hermes.passDataToKeytar", "false") === "true",
      pastTestStorageNames: getProp(tgamConfig, "hermes.pastTestStorageKeys", "").split(","),
      storageNameAnon: getProp(tgamConfig, "hermes.currentTestStorageKeyAnon", ""),
      storageNameReg: getProp(tgamConfig, "hermes.currentTestStorageKeyReg", ""),
      algorithm: {
        name: "Hermes2" // default algorithm for anonymous customers
      }
    };
    this._enabled = this.shouldRun();
    this._hideSubscriberKeys = this.props.hideSubscriberKeys;
    this._passDataToKeytar = this.shouldPassDataToKeytar();
  }

  /**
   * The Hermes API endpoint, with query string parameters in place.
   * @returns {String} - an absolute API url
   */
  getEndpoint() {
    logger.info(`${debugTag} this.algorithmName(): ${this.algorithmName()}`);
    const algorithm = this.algorithmName();
    let params = [];
    let endpoint = API_DS_DYNAMIC_PAYWALL;
    if (algorithm == "Hermes3") {
      params = [
        this.props.sophi3Id ? `domain_userid=${this.props.sophi3Id}` : null,
        this.props.hashId ? `user_id=${this.props.hashId}` : null,
        this.props.contentId ? `content_id=${this.props.contentId}` : null,
        this.props.pageType ? `page_type=${this.props.pageType}` : null
      ].filter(param => param !== null);
      endpoint = API_DS_SMARTWALL;
    } else {
      params = [
        this.props.contentId ? `article_id=${this.props.contentId}` : null,
        this.props.sophiId ? `sophi_id=${this.props.sophiId}` : null,
        this.props.hashId ? `hash_id=${this.props.hashId}` : null
      ].filter(param => param !== null);
    }

    params.push(
      `algorithm_name=${this.algorithmName()}`,
      this.props.localTime ? `localtime=${encodeURIComponent(this.props.localTime)}` : null,
      this.props.section ? `section=${encodeURIComponent(this.props.section)}` : null,
      this.props.referrer ? `referrer=${encodeURIComponent(this.props.referrer)}` : null
    );
    return `${endpoint}/?${params.join("&")}`;
  }

  /**
   * Get the name of the algorithm that is being used for the current
   * customer experience. For these customers, if a value
   * cannot be found in localStorage the class default will be returned.
   * @returns {String|null} - the algorithm name
   */
  algorithmName() {
    const algo = isRegistered() ? local.get(this.props.storageNameReg) : local.get(this.props.storageNameAnon);
    logger.info(`${debugTag} algorithm retrieved from storage is`, algo);
    return isNil(algo) ? this.props.algorithm.name : algo;
  }

  /**
   * Get the algorithm name that was used. Will be merged with the
   * Hermes API response and ultimately passed to Keytar so that
   * we can report to analytics which algorithm was used for this customer.
   * @returns {String|null}
   */
  get algorithm() {
    return this.algorithmName();
  }

  /**
   * Formats the current page's section name in a way that's meaningful to Hermes
   * @param {String} section - the current page's section
   * @returns {String} - the current page's section with no leading slash
   */
  getSection(section) {
    return section.startsWith("/") ? section.split("/")[1] : section.split("/")[0];
  }

  /**
   * Formats the user's current time in a way that's meaningful to Hermes
   * @param {Date} date - the current time as a Date object
   * @returns {String} - a formatted version of the current time
   */
  getLocalTime(date) {
    return format(date, "YYYY-MM-DD HH:mm:ss [GMT]ZZ");
  }

  /**
   * Derives a value from the referrer that's meaningful to Hermes
   * @param {String} referrer - the actual referrer value from the browser
   * @returns {String} - a simplified/filtered value that Hermes can understand
   */
  getReferrer(referrer) {
    if (!referrer) {
      return "no";
    } else if (referrer.includes("facebook.com")) {
      return "facebook";
    } else if (referrer.includes("google.com")) {
      return "google";
    } else {
      return "GAM and other";
    }
  }

  /**
   * Ensure that the Hermes API will only called on the appropriate types of pages
   * @param {String} url
   * @returns {Boolean}
   */
  urlIsEligible(url = window.location.toString()) {
    const invalidUrlPatterns = [
      /^https:\/\/([\w+\d+\-\.])+\.tgam\.arcpublishing\.com\/(pb|ellipsis|composer|pf)?/im,
      /.*?\/globe\-advisor\/([\w\-]+\/)*(article|gallery|video)+([\w\-]+)\/*/im,
      /.*?\/appview\/([\w\-]+\/)*(article|gallery|video)+([\w\-]+)\/*/im
    ];
    return invalidUrlPatterns.every(re => re.test(url) === false);
  }

  /**
   * Set the enabled state
   * @param {Boolean} val
   */
  set enabled(val) {
    this._enabled = val;
  }

  /**
   * Get the enabled state
   * @returns {Boolean}
   */
  get enabled() {
    return this._enabled;
  }

  /**
   * Get the "pass data to Keytar" state
   * @returns {Boolean}
   */
  get passDataToKeytar() {
    return this._passDataToKeytar;
  }

  /**
   * Get the "hide subscriber keys" state
   * @returns {Boolean}
   */
  get hideSubscriberKeys() {
    return this._hideSubscriberKeys;
  }

  /**
   * Get the "force run" value. True, false, or (null) not set.
   * @returns {Boolean|null}
   */
  static get forceRun() {
    let ret = null;
    if (/hermes=true/.test(window.location.search)) {
      ret = true;
    } else if (/hermes=false/.test(window.location.search)) {
      ret = false;
    }
    return ret;
  }

  /**
   * Get the "force mock" flag.
   * A developer backdoor to mock the Hermes API response, via window.localStorage.
   * Response should be in: tgam.mock.hermes;
   * @returns {Boolean}
   */
  static get forceMock() {
    return /hermesMock=true/.test(window.location.search) && isLocalStorageTypeSupported("localStorage");
  }

  /**
   * When Hermes is enabled, attempt to fetch data about the current sophi user/content id from the
   * data science API.
   *
   * In the event of an erroneous response code returned from the service or the request taking
   * longer than the specified timeout, resolve with no data.
   *
   * If the API does not respond within this.props.requestTimeout limit, an async setTimeout is
   * created and we mark "hermes done" in its callback. This should provide us with the ability to
   * track within SpeedCurve how often the API exceeds this.props.requestTimeout.
   *
   * At some point in the future we'll want to implement window.AbortController as a method to
   * timeout the fetch request.
   *
   * See: https://flaviocopes.com/fetch-api/#how-to-cancel-a-fetch-request
   * Until then, we rely on Promise.race to fake timeout behaviour.
   *
   * http://www.sheshbabu.com/posts/working-with-fetch-api/#Timeouts.
   * Note that this behaviour will not actually abort the request.
   *
   * @returns {Promise}
   */
  fetch() {
    if (!this.enabled) {
      logger.info(`${debugTag} is not enabled.`);
      return Promise.resolve();
    }
    if (this.props.forceMock) {
      let mock = window.localStorage.getItem("tgam.mock.hermes");
      mock = JSON.parse(mock);
      logger.info(`${debugTag} response mocked:`, mock);
      return Promise.resolve(mock);
    }
    return new Promise(resolve => {
      mark("hermes_started");
      Promise.race([fetch(this.getEndpoint()), fetchTimeout(this.props.requestTimeout)])
        .then(responseHandler)
        .then(data => {
          resolve(data);
          mark("hermes_done");
          this.logPerformance({ inTestGroup: true });
          logger.info(`${debugTag} Response received within ${this.props.requestTimeout}ms`);
        })
        .catch(err => {
          resolve(null);
          if (didTimeOut && this.props.forceRun) {
            // synthetic measurement in SpeedCurve
            setTimeout(() => {
              mark("hermes_done");
              this.logPerformance({ didTimeOut });
            }, 3000);
            logger.error(`${debugTag} ${err}`);
            return;
          }
          mark("hermes_done");
          const error = err.toString();
          const fetchError = error.toLowerCase().includes("failed to fetch");
          if (!fetchError) {
            this.logPerformance({ didTimeOut, error });
          }
          logger.error(`${debugTag} ${err}`);
        });
    });
  }

  /**
   * Dispatch a custom event that analytics/CEM can listen for
   * @param {Boolean|null} inTestGroup - `fetch` API response or `null` if `fetch` fails
   */
  dispatchEvent() {
    const e = new CustomEvent("hermesComplete", {
      detail: {
        userInTest: true
      }
    });
    window.dispatchEvent(e);
  }

  /**
   * Measure and log the user timing API data that has been collected
   * If the user is within the test scope or the Hermes API timed out then always push to analytics
   * Otherwise only push to analytics for 10% of the real-word traffic
   * @param {Object} c
   * @param {Boolean} [c.inTestGroup=false] - true if the user is within the test scope
   * @param {Boolean} [c.timeoutError=false] - true if Hermes API call exceeded the timeout
   * @param {String} [c.error=""] - the error message
   */
  logPerformance({ inTestGroup = false, timeoutError = false, error = "" }) {
    measure("hermes");
    const entries = getEntriesByName("hermes");
    if (!entries.length) {
      return;
    }
    const duration = parseInt(entries[0].duration, 10);
    logger.info(`${debugTag} Timing: ~ ${duration}ms`);
    if (this.props.forceRun) {
      return;
    }
    let eventData = {
      category: "hermes",
      action: "performance"
    };
    if (timeoutError) {
      eventData.label = "Exceeded";
      eventData.value = this.props.requestTimeout;
    } else if (error !== "") {
      eventData.label = `Failure ${error}`;
    } else {
      eventData.label = "API response time";
      eventData.value = duration;
    }

    // Report analytics to GTM if:
    // 1. the user is within the test group,
    // 2. the API timed out
    // 3. there was an error (not timeout)
    // 4. for 10% of real-world traffic
    if (inTestGroup || timeoutError || error !== "" || parseInt(100 * Math.random(), 10) <= 10) {
      window.analyticsLayer.push({
        event: "simpleEvent",
        simpleEvent: eventData
      });
    }
  }

  /**
   * Determine if Hermes should run in the current context
   * @returns {Boolean}
   */
  shouldRun() {
    const epochNow = Math.floor(new Date().getTime() / 1000);
    const timeDisabled= isTimedDisabled(tgamConfig, epochNow, "hermes.hermesDisableStart", "hermes.hermesDisableEnd");
    logger.info(`${debugTag} isTimedDisabled`, timeDisabled);
    if (timeDisabled){
      return false;
    }
    // clean up from previous tests
    this.props.pastTestStorageNames.forEach(k => {
      try {
        local.remove(k);
      } catch (e) {}
    });

    const currentTestKeyAnon = local.get(this.props.storageNameAnon);
    const currentTestKeyReg = local.get(this.props.storageNameReg);
    // despite a stringy value being stored in localStorage, the lib/store/get function does
    // JSON.parse(value) and for a stringy boolean, the result of that function is an actual boolean o_0
    let userIsInTest = (isRegistered() && !isNil(this.props.hashId) && !isNil(currentTestKeyReg)) || (isAnonymous() && !isNil(currentTestKeyAnon));
    if (!userIsInTest && (isRegistered() && !isNil(this.props.hashId) || isAnonymous())) {
      const storageName = isRegistered() ? this.props.storageNameReg : this.props.storageNameAnon;
      const sampleRate = isRegistered() ? this.props.sampleRate.REG : this.props.sampleRate.ANON;
      const sampleRateV3 = isRegistered() ? this.props.sampleRate.REG_V3 : this.props.sampleRate.ANON_V3;
      // we know that the customer hasn't visited since this test started since
      // there is no 'currentTestKeyReg' value in localStorage
      const sample = parseInt(100 * Math.random(), 10);
      const rate = sampleRate;
      const useAlgorithm3 = sample <= sampleRateV3;
      logger.info(`${debugTag} sample: ${sample}, Hermes 2 rate: ${rate}, Hermes 3 rate: ${sampleRateV3}`);

      if (useAlgorithm3) {
        this.props.algorithm.name = "Hermes3";
      }
      logger.info(`${debugTag} customer algorithm name: ${this.props.algorithm.name}`);
      // setting the algorithm name to the Hermes API version instead of true/false will keep the user in that test segment
      // for each anonymous and registered states
      local.set(storageName, this.props.algorithm.name);
      userIsInTest = true; // setting userIsInTest to true is a condition to call the Hermes API below
    }

    const conditions = {
      isTurnedOn: this.props.isTurnedOn === true,
      hasSophiId: !isNil(this.props.sophiId),
      userIsInTest,
      urlEligible: this.urlIsEligible() === true
    };
    const condRun = values(conditions).every(item => item === true);
    const forceRun = this.props.forceRun;
    const run = isNil(forceRun) ? condRun : forceRun;
    logger.info(`${debugTag} should run`, { run, forceRun, condRun }, conditions);

    return run;
  }

  /**
   * Determine if Hermes should pass its data to Keytar
   * @returns {Boolean}
   */
  shouldPassDataToKeytar() {
    const conditions = {
      runtimePropEnabled: this.props.passDataToKeytar === true,
      isStory: getProp(tgamConfig, "meta.pagetype", null) === "story",
      isNotAPersistentContentColour: ["green"].includes(this.props.colour) === false,
      isNotAdvContent: this.props.advContent === false,
      isNotAdvEditorialContent: this.props.keywords.toLowerCase().includes("adveditorial") === false,
      isNotHpDc: isHousePriceDataCentre() === false,
      isNotFreeToViewReferrer: isFreeToViewReferrer() === false
    };
    const shouldPassData = values(conditions).every(item => item === true);
    logger.info(`${debugTag} should pass data to Keytar`, shouldPassData, conditions);
    return shouldPassData;
  }

}
